import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiClient } from "../../api";
import { decodeUuid, slugifyEventName } from "../../utils/eventShareId";

/**
 * Resolves short share links and redirects to canonical /event/:name/:id.
 *
 * Supported formats:
 * 1) /e/:code/:name  — code is compressed restaurantId (no API needed)
 * 2) /e/:code        — code is compressed EventId → restaurantevent/link/{eventId}
 */
export default function ShortEventRedirect() {
  const { code, name: nameParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const go = (slug, restaurantId) => {
      if (!slug || !restaurantId) {
        setError("Event not found.");
        return;
      }
      const qs = searchParams.toString();
      navigate(`/event/${slug}/${restaurantId}${qs ? `?${qs}` : ""}`, {
        replace: true,
      });
    };

    const resolve = async () => {
      const decoded = decodeUuid(code);
      if (!decoded) {
        if (!cancelled) setError("This event link is invalid.");
        return;
      }

      // Self-contained short link: /e/{compactRestaurantId}/{eventSlug}
      if (nameParam) {
        go(nameParam, decoded);
        return;
      }

      // EventId-only short link: needs link API
      try {
        const res = await apiClient.get(`restaurantevent/link/${decoded}`);
        if (cancelled) return;

        if (!res?.data?.status || !res?.data?.result) {
          setError(
            res?.data?.error_message ||
              "Event not found. This short link may need an updated share URL.",
          );
          return;
        }

        const { EventName, RestaurantId } = res.data.result;
        go(slugifyEventName(EventName), RestaurantId);
      } catch {
        if (!cancelled) {
          setError(
            "Event not found. Please use a newer share link from the restaurant dashboard.",
          );
        }
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [code, nameParam, navigate, searchParams]);

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 2,
      }}
    >
      {error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <>
          <CircularProgress size={28} />
          <Typography color="text.secondary">Opening event…</Typography>
        </>
      )}
    </Box>
  );
}
